import {
  Button,
  formatNumber,
  formatPercent,
  generateLinkWithParams,
  getDateWithHumanReadableTime,
  ItemSplitter,
  Link,
  linkPlaceholders,
  OverflowContainer,
  routing,
  RowFlex,
  Spinner,
  Table,
  TextField,
  theme,
  useAxios,
  useLoginContext,
  useModalContext,
} from "common";
import { format } from "date-fns";
import ExcelJS from "exceljs";
import { capitalize } from "lodash";
import Tooltip from "rc-tooltip";
import { Fragment, useEffect, useRef, useState } from "react";
import { CgNotes } from "react-icons/cg";
import {
  FaCheckCircle,
  FaEdit,
  FaFileExcel,
  FaFilePdf,
  FaPlus,
  FaTrash,
} from "react-icons/fa";
import generatePDF from "react-to-pdf";
import AddGoal from "./AddGoal";
import { GoalProgress } from "./GoalView/styles";
import {
  eBrandGoalTypeDescriptionSingle,
  GoalChip,
  poStatusMap,
} from "./static";

const SoTooltip = ({ sos = [] }) =>
  sos.map((so) => (
    <div
      style={{ color: theme.colors.white, marginBottom: "1rem" }}
      key={so.OrderNumber}
    >
      <h3 style={{ color: theme.colors.white }}>
        {so.OrderNumber} - ${formatNumber(so.Amount)} -{" "}
        {getDateWithHumanReadableTime(so.OrderDate)}
      </h3>
      {so.Customer && (
        <div style={{ color: theme.colors.white }}>
          Customer: {so.Customer.CustomerName}
        </div>
      )}
      {so.Customer?.salesRep && (
        <div style={{ color: theme.colors.white }}>
          Sales Rep: {so.Customer.salesRep.name ?? ""}
        </div>
      )}
      {so.purchaseOrders?.length > 0 && (
        <div style={{ color: theme.colors.white }}>
          <div style={{ color: theme.colors.white }}>POs:</div>
          {so.purchaseOrders.map((po) => (
            <div style={{ color: theme.colors.white }} key={po.PONumber}>
              {po.PONumber} - {poStatusMap[po.Status]}
            </div>
          ))}
        </div>
      )}
    </div>
  ));

const downloadExcel = async (data = [], filename = "goals.xlsx") => {
  if (data.length === 0) return;

  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet("Goals");

  // Add header row
  worksheet.columns = Object.keys(data[0]).map((key) => ({
    header: capitalize(key),
    key: key,
    width: 20, // optional column width
  }));

  // Add data rows
  data.forEach((item) => {
    worksheet.addRow(item);
  });

  // Generate buffer and download
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });

  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  link.click();
  URL.revokeObjectURL(link.href);
};

const TableView = ({ goals: allGoals = [], refetch, thisDate }) => {
  const pdfRef = useRef();
  const [pdfPrint, setPdfPrint] = useState(false);
  const dateFileName = format(thisDate, "MMMM yyyy");

  useEffect(() => {
    if (!pdfPrint) return;
    generatePDF(pdfRef, {
      filename: `Goals ${dateFileName}.pdf`,
    });
    setTimeout(() => setPdfPrint(false), 1000);
  }, [pdfPrint, dateFileName]);

  const { currentUser } = useLoginContext();
  const { closeModal, setModalContent } = useModalContext();
  const [search, setSearch] = useState("");

  const { callAxios, loading } = useAxios({
    onComplete: () => {
      refetch();
    },
  });
  const goalProps = {
    close: closeModal,
    onCompleted: () => {
      refetch();
      closeModal();
    },
  };

  const [zeroGoals, someGoals, successGoals] = [[], [], []];

  for (const goal of allGoals.filter(
    (g) =>
      !search || g.brand?.name?.toLowerCase().includes(search.toLowerCase())
  )) {
    const arrToUse =
      goal.currentAmount === 0
        ? zeroGoals
        : goal.currentAmount >= goal.goalAmount
        ? successGoals
        : someGoals;
    arrToUse.push(goal);
  }

  const goalData = [
    { goals: zeroGoals, heading: "Goals With No Progress" },
    { goals: someGoals, heading: "Incomplete Goals" },
    { goals: successGoals, heading: "Completed Goals" },
  ];

  return (
    <Fragment>
      <RowFlex extend>
        <TextField
          placeholder="Search"
          value={search}
          onChange={(v) => setSearch(v)}
        />
        <RowFlex>
          {[
            "simon@hbamfl.com",
            "joseph@hbamfl.com",
            "hurwitzse@gmail.com",
          ].includes(currentUser.email) && (
            <Button
              fit
              onClick={() =>
                setModalContent(
                  <AddGoal
                    {...goalProps}
                    thisDate={thisDate}
                    currentGoals={allGoals}
                  />
                )
              }
            >
              <FaPlus />
            </Button>
          )}
          <Button
            fit
            onClick={() => {
              const data = goalData.flatMap(({ goals, heading }) =>
                goals.map((goal) => ({
                  heading,
                  brand: goal.brand?.name ?? "",
                  type: eBrandGoalTypeDescriptionSingle[goal.type],
                  current: goal.currentAmount,
                  goal: goal.goalAmount,
                  notes: goal.notes,
                  repeat: !!goal.repeat,
                  progress: formatPercent(goal.currentAmount, goal.goalAmount),
                }))
              );
              downloadExcel(data, `Goals - ${dateFileName}.xlsx`);
            }}
          >
            <FaFileExcel />
          </Button>
          <Button fit onClick={() => setPdfPrint(true)}>
            <FaFilePdf />
          </Button>
        </RowFlex>
      </RowFlex>
      <ItemSplitter
        split={3}
        ref={pdfRef}
        style={pdfPrint ? { gridTemplateColumns: "1fr" } : {}}
      >
        {goalData.map(({ goals, heading }) => {
          return (
            <OverflowContainer key={heading}>
              <Table>
                <thead>
                  <tr>
                    <th colSpan={10} style={{ fontSize: "0.8rem" }}>
                      {heading}
                    </th>
                  </tr>
                  <tr>
                    <th style={{ width: pdfPrint ? "60%" : "20%" }}>Brand</th>
                    <th style={{ width: "9%" }}>Type</th>
                    <th style={{ width: "1%" }}>Current</th>
                    <th style={{ width: "1%" }}>Goal</th>
                    <th style={{ width: "10%" }} />
                    <th style={{ width: "1%" }}>SOs</th>
                    <th style={{ width: "1%" }} />
                  </tr>
                </thead>
                <tbody>
                  {goals.map((g) => {
                    return (
                      <tr key={g.id}>
                        <td>
                          <Link
                            target="_blank"
                            to={generateLinkWithParams(routing.brands.view, {
                              [linkPlaceholders.brandId]: g.brand?.id ?? "",
                            })}
                          >
                            {g.brand?.name}
                          </Link>
                        </td>
                        <td>
                          <GoalChip type={g.type} />
                        </td>
                        <td>${formatNumber(g.currentAmount)}</td>
                        <td>${formatNumber(g.goalAmount)}</td>
                        <td>
                          <GoalProgress
                            percent={formatPercent(
                              g.currentAmount,
                              g.goalAmount
                            )}
                          />
                        </td>
                        <td>
                          {g.sos.length > 0 && (
                            <Tooltip overlay={<SoTooltip sos={g.sos} />}>
                              <div>{g.sos.length}</div>
                            </Tooltip>
                          )}
                        </td>
                        <td>
                          <RowFlex>
                            {loading ? (
                              <Spinner inline />
                            ) : (
                              <Fragment>
                                <FaEdit
                                  cursor="pointer"
                                  onClick={() =>
                                    setModalContent(
                                      <AddGoal {...goalProps} goal={g} />
                                    )
                                  }
                                />
                                <FaTrash
                                  cursor="pointer"
                                  fill={theme.colors.danger}
                                  onClick={() => {
                                    callAxios({
                                      method: "DELETE",
                                      url: `/brandGoals/${g.id}`,
                                    });
                                  }}
                                />
                                {g.repeat && (
                                  <Tooltip overlay="Recurring">
                                    <FaCheckCircle
                                      fill={theme.colors.success}
                                    />
                                  </Tooltip>
                                )}
                                {g.notes && (
                                  <Tooltip
                                    overlay={
                                      <div
                                        style={{
                                          background: "white",
                                          padding: "0.3rem",
                                          borderRadius: "4px",
                                        }}
                                        dangerouslySetInnerHTML={{
                                          __html: g.notes,
                                        }}
                                      />
                                    }
                                  >
                                    <CgNotes />
                                  </Tooltip>
                                )}
                              </Fragment>
                            )}
                          </RowFlex>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </OverflowContainer>
          );
        })}
      </ItemSplitter>
    </Fragment>
  );
};

export default TableView;
